import {
	Avatar,
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	Link,
} from "@mui/material";
import { PrimaryButton } from "../CustomButton";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import EventIcon from "@mui/icons-material/Event";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
	textNeutralTertiary,
} from "../../../core/theme";
import StyledTextarea from "../StyledTextarea";
import { useFormik } from "formik";
import * as yup from "yup";

import { useSelector } from "react-redux";
import { If } from "../If";

import { formatStringDate } from "../../utils/dateUtils";
import { stringForAvatar } from "../../../features/tender_page/utils/workflowView.util";
import DeleteIcon from "@mui/icons-material/Delete";
import { ActionConfirmationModal } from "../ActionConfirmationModal";
import { selectAuthenticatedUser } from "../../../features/authentication/auth.selector";
import canUserDeleteComment from "./activityModal.util";
import LocalTime from "../LocalTime/LocalTime";
import { style } from "./ActivityModal.style";
import { useCreateCommentMutation, useLazyGetCommentsQuery, useDeleteCommentMutation } from "../../../requests_cm/gepoCommentService/service";
import { CommentObjectClass, CommentType, Comment } from "../../../requests_cm/gepoCommentService/types";

export interface ActivityModalProps {
	onClose: () => void;
	isOpen: boolean;
	resourceId: number | undefined;
	commentClass: CommentObjectClass;
	resourceName: string | undefined;
}

export interface SystemCommentProps {
	resourceName: string;
	comment: Comment;
}

function ActivityModal(props:  ActivityModalProps) {
	const { onClose, isOpen, resourceName, resourceId, commentClass } = props;
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	const [commentIdToDelete, setCommentIdToDelete] = useState<number | null>(
		null
	);
	const currentUser = useSelector(selectAuthenticatedUser);
	let validationSchema = yup.object({
		comment: yup.string().required("Comment is a required field"),
	});

	const [
			getComments,
			{ data: comments, isLoading: isGetCommentLoading },
		] = useLazyGetCommentsQuery();


	const [createComment, { isLoading: isCreateCommentLoading }] =
			useCreateCommentMutation();


	const [deleteComment, { isLoading: isDeleteCommentLoading }] =
			useDeleteCommentMutation();




	const [page, setPage] = useState(1);

	let formik = useFormik({
		initialValues: {
			comment: "",
		},
		validationSchema: validationSchema,
		enableReinitialize: true,
		onSubmit: (values: { comment: string }, { resetForm }) => {
			if (resourceId) {
				createComment({object_class: commentClass, object_id:resourceId, comment:values.comment})
				resetForm();
			}
		},
	});

	const loader = useMemo(() => {
		return isGetCommentLoading || isCreateCommentLoading || isDeleteCommentLoading
	}, [isGetCommentLoading, isCreateCommentLoading, isDeleteCommentLoading])

	useEffect(() => {
		if (resourceId && commentClass) {
			 getComments({object_id: resourceId, object_class: commentClass, page, per_page:20 })
		}
	}, [resourceId, commentClass, page]);

	const closeModal = () => {
		setPage(1);
		onClose();
	};

	const openDeleteCommentConfirmation = (commentId: number) => {
		setCommentIdToDelete(commentId);
	};
	const deleteCommentConfirmed = useCallback(() => {
		if (commentIdToDelete && resourceId)
			deleteComment({id:commentIdToDelete});
	}, [commentIdToDelete, resourceId]);

	const deleteCommentUnconfirmed = () => {
		setCommentIdToDelete(null);
	};

	return (
		<>
			<Dialog
				PaperProps={{ sx: style.dialog }}
				maxWidth="md"
				onClose={closeModal}
				open={isOpen}
			>
				<DialogTitle sx={style.dialogTitle}>
					<Box alignItems={"center"} display={"flex"}>
						<MessageOutlinedIcon className="message-icon" />
						Activity
					</Box>
					<IconButton
						onClick={() => {
							closeModal();
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Box sx={style.name}>
						{commentClass}: <Box component={"span"}>{resourceName}</Box>
					</Box>

					{comments?.data.map((comment: Comment) => (
						<>
							<If condition={comment.type === CommentType.SYSTEM}>
								<Box sx={style.systemComment}>
									<EventIcon />
									<Box>
										<Box component={"span"}>
											{comment.creator.first_name}&nbsp;
											{comment.creator.last_name}&nbsp;
										</Box>
										{comment.comment}
										<LocalTime utcDate={comment.created_at}>
											<Box sx={style.createdAt}>
												{formatStringDate(
													comment.created_at,
													"dd/MM/yyyy, HH:mm",
													timezone
												)}
											</Box>
										</LocalTime>
									</Box>
								</Box>
							</If>

							<If condition={comment.type === CommentType.USER}>
								<Box sx={style.manualComment}>
									<Box>
										<Avatar
											{...stringForAvatar(
												`${comment.creator?.first_name} ${comment.creator?.last_name}`
											)}
											sx={style.avatar}
										/>
										<Box className="comment-reated-at">
											<Box
												component={"span"}
												className="creator-name"
											>
												{comment.creator.first_name}
												&nbsp;
												{comment.creator.last_name}
											</Box>
											&nbsp; added a comment
											<LocalTime
												utcDate={comment.created_at}
											>
												<Box sx={style.createdAt}>
													{formatStringDate(
														comment.created_at,
														"dd/MM/yyyy, HH:mm",
														timezone
													)}
												</Box>
											</LocalTime>
										</Box>
									</Box>
									<Box sx={style.commentContent}>
										<Box component={"span"}>
											{comment.comment}
										</Box>
										{canUserDeleteComment(
											comment.creator,
											currentUser
										) && (
											<IconButton
												disabled={loader}
												onClick={() => {
													openDeleteCommentConfirmation(
														comment.id
													);
												}}
											>
												<DeleteIcon />
											</IconButton>
										)}
									</Box>
								</Box>
							</If>
						</>
					))}
					{loader && (
						<Box display={"flex"} justifyContent={"center"}>
							<CircularProgress
								sx={{ margin: "40px" }}
								data-testid="loader"
							/>
						</Box>
					)}
					{!loader && !comments?.data.length && (
						<Box
							display={"flex"}
							margin={"20px"}
							justifyContent={"center"}
							color={textNeutralTertiary}
						>
							No Comments found
						</Box>
					)}
					{!loader && comments?.has_more && (
						<Box
							display={"flex"}
							margin={"12px"}
							justifyContent={"center"}
							color={textNeutralTertiary}
						>
							<Link
								sx={{ cursor: "pointer" }}
								onClick={() => {
									setPage((prevPage) => prevPage + 1);
								}}
							>
								Load more
							</Link>
						</Box>
					)}

					<form
						key={`form-action-pricing-modal`}
						onSubmit={formik.handleSubmit}
					>
						<Box sx={style.commentform}>
							<FormControl
								error={!!formik.errors?.comment}
								sx={{ width: "100%" }}
								variant="standard"
							>
								<StyledTextarea
									onChange={formik.handleChange}
									value={formik.values?.comment}
									name="comment"
									placeholder="Tap your comment here"
									minRows={3}
									sx={{ width: "100%" }}
									disabled={!!loader}
								/>
								{!!formik.errors?.comment && (
									<FormHelperText id="error-text">
										{formik.errors?.comment || ""}
									</FormHelperText>
								)}
							</FormControl>
							<PrimaryButton
								disabled={!!loader}
								sx={{ minWidth: "100px" }}
								text="send"
								type="submit"
								color="primary"
							></PrimaryButton>
						</Box>
					</form>
				</DialogContent>
			</Dialog>
			<ActionConfirmationModal
				title="Confirmation"
				text="Do you want to delete this comment?"
				isOpen={!!commentIdToDelete}
				onConfirm={deleteCommentConfirmed}
				onClose={deleteCommentUnconfirmed}
			/>
		</>
	);
}

export default memo(ActivityModal);
